import React, { useCallback, useEffect, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { mergeRegister } from '@lexical/utils';
import {
  $getNodeByKey, $getSelection, $isNodeSelection,
  CLICK_COMMAND, COMMAND_PRIORITY_LOW, KEY_BACKSPACE_COMMAND, KEY_DELETE_COMMAND,
} from 'lexical';
import { buildEmbedSrc } from '../../../utils/videoEmbeds';

/**
 * The React face of a DocVideoEmbedNode: a 16:9 responsive box with the
 * provider's player iframe. The src is rebuilt from the node's parsed parts
 * on every render (never a stored URL string).
 *
 * Selection/removal is PageLinkComponent's pattern. One honest limitation:
 * clicks INSIDE the iframe land in the provider's document and never reach
 * Lexical's CLICK_COMMAND, so selecting the block means clicking its border
 * area (or dragging over it) — same tradeoff as the Lexical playground's
 * embed nodes.
 *
 * A degraded node (invalid id -> no src) renders nothing visible but stays
 * selectable/removable so the empty block can be deleted.
 */
export default function DocVideoEmbedComponent({ nodeKey, provider, videoId, start }) {
  const [editor] = useLexicalComposerContext();
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const boxRef = useRef(null);

  const src = buildEmbedSrc({ provider, id: videoId, start });
  const title = provider === 'vimeo' ? 'Vimeo video' : 'YouTube video';

  const onDelete = useCallback(
    (event) => {
      if (isSelected && $isNodeSelection($getSelection())) {
        if (event) event.preventDefault();
        const node = $getNodeByKey(nodeKey);
        if (node) node.remove();
        return true;
      }
      return false;
    },
    [isSelected, nodeKey]
  );

  const onClickCommand = useCallback(
    (event) => {
      if (boxRef.current && (event.target === boxRef.current || boxRef.current.contains(event.target))) {
        if (!event.shiftKey) clearSelection();
        setSelected(true);
        return true;
      }
      return false;
    },
    [clearSelection, setSelected]
  );

  useEffect(
    () =>
      mergeRegister(
        editor.registerCommand(CLICK_COMMAND, onClickCommand, COMMAND_PRIORITY_LOW),
        editor.registerCommand(KEY_DELETE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
        editor.registerCommand(KEY_BACKSPACE_COMMAND, onDelete, COMMAND_PRIORITY_LOW)
      ),
    [editor, onClickCommand, onDelete]
  );

  return (
    <div
      ref={boxRef}
      className={`lx-doc-video${isSelected ? ' lx-doc-video--selected' : ''}`}
    >
      {/* `allow` mirrors chat's LinkPreview embeds (ChatMessageView) for
          feature parity — autoplay/PiP behave the same in both surfaces. */}
      {src && (
        <iframe
          src={src}
          title={title}
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      )}
    </div>
  );
}
